import { Link } from "react-router-dom";
import { Check, HeartHandshake } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { CTABanner } from "@/components/site/CTABanner";
import { Seo } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import { IMAGES } from "@/lib/site";

const SERVICES = [
  "General home cleaning",
  "Vacuuming",
  "Mopping",
  "Kitchen cleaning",
  "Bathroom cleaning",
  "Dusting",
  "General household cleaning",
  "Other agreed cleaning tasks",
];

const Care = () => (
  <>
    <Seo
      title="NDIS & Aged Care Cleaning Sydney | Respectful Home Support | Oz SpotNest"
      description="Respectful cleaning and home maintenance support for NDIS participants, aged-care clients and families across Sydney, NSW. Discuss your cleaning support needs today."
    />
    <PageHero
      eyebrow="NDIS & Aged Care"
      title="Respectful Cleaning &"
      highlight="Home Maintenance Support"
      description="Cleaning support designed for people who need a hand maintaining a clean and comfortable home — including NDIS participants, aged-care clients, families and care coordinators."
    />

    <section className="section-pad" data-testid="care-services-section">
      <div className="container-site grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="overflow-hidden rounded-[2.5rem] shadow-2xl shadow-sky-900/10 ring-1 ring-slate-100">
            <img
              src={IMAGES.care}
              alt="Friendly home support helping keep a comfortable home clean"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover"
            />
          </div>
        </Reveal>
        <div>
          <Reveal>
            <span className="eyebrow">How we can help</span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
              Cleaning support, shaped around the person
            </h2>
            <p className="mt-5 text-base leading-relaxed sm:text-lg">
              Cleaning and home maintenance support may be available for eligible participants
              depending on their plan, funding arrangements and service requirements. Talk to us
              about your situation and we'll let you know how we can help.
            </p>
            <ul className="mt-7 grid gap-3 sm:grid-cols-2">
              {SERVICES.map((s) => (
                <li key={s} className="flex items-start gap-2.5 text-sm text-slate-600">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" strokeWidth={3} />
                  {s}
                </li>
              ))}
            </ul>
            <div className="mt-9">
              <Link
                to="/contact?service=NDIS%20Cleaning"
                data-testid="care-page-discuss-button"
                className="btn-primary"
              >
                Discuss Your Cleaning Needs
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="section-pad bg-slate-50" data-testid="care-approach-section">
      <div className="container-site max-w-4xl text-center">
        <Reveal>
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
            <HeartHandshake className="h-7 w-7" />
          </span>
          <h2 className="mt-6 text-3xl font-bold tracking-tight sm:text-4xl">A Respectful Approach</h2>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed sm:text-lg">
            Every home and every person's needs are different. We aim to provide cleaning support
            with respect, patience, clear communication and consideration for individual preferences.
          </p>
        </Reveal>
      </div>
    </section>

    <CTABanner
      heading="Let's Talk About the Support You Need"
      text="Tell us about the home and the kind of cleaning support that would help — we'll take it from there."
      ctaLabel="Discuss Your Cleaning Support Needs"
      service="NDIS Cleaning"
    />
  </>
);

export default Care;
